import { Component, ElementRef, Input, ViewChild, OnChanges, ChangeDetectorRef, SimpleChanges, Output, EventEmitter, Renderer2, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ImageUploadService } from 'src/app/services/upload-service.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { GiftCardsService } from 'src/app/services/gift-cards.service';
import { DateFormatterService } from 'src/app/services/date-formatter.service';
import { AngularFireStorage } from '@angular/fire/compat/storage';

@Component({
  selector: 'app-gift-cards',
  templateUrl: './gift-cards.component.html',
  styleUrls: ['./gift-cards.component.scss']
})
export class GiftCardsComponent implements OnChanges, OnInit {

  @ViewChild('giftExpand') giftExpand!: ElementRef;

  giftCardForm: FormGroup;
  selectedMedia: { name: string, size: number, invalid: boolean, errorMessage?: string, thumbnail?: any }[] = [];
  validFiles: File[] = [];
  mediaValid: boolean = true;
  mediaUploaded: boolean = false;

  @Input() giftCards: any;
  @Input() businessId!: string;
  @Input() hasBirthdayCards!: boolean;
  @Input() hasExtraGameCards!: boolean;
  @Input() hasOtherCards!: boolean;
  @Input() activationCode!: string;
  @Input() activeTabFromParent!: boolean;

  newTabStatus!: boolean;
  loading: boolean = false; 
  filteredGiftCards: any[] = [];
  isActiveTab: boolean = true;
  @Output() giftCardAdded = new EventEmitter<void>();
  @Output() newTab = new EventEmitter<boolean>();
  editMode: boolean = false;
  selectedCard: any = null;

  setActiveTab(isActive: boolean): void {
    this.isActiveTab = isActive;
    this.filterGiftCards();
  }

  checkTabStatus(newTabStatus: boolean) {
    this.newTabStatus = newTabStatus;
  }

  // Function to filter vouchers based on active state
  filterGiftCards(): void {
    this.filteredGiftCards = this.giftCards.filter(
      (card: { active: boolean; }) => card.active === this.isActiveTab
    );
  }

  getFilteredGiftCards(type: string): any[] {
    return this.filteredGiftCards.filter((card: any) => card.type === type && card.active === this.isActiveTab);
  }

  @ViewChild('backdrop') backdrop!: ElementRef;

  toggleBackdrop(show: boolean): void {
    if (show) {
      this.backdrop.nativeElement.style.display = 'block';
    } else {
      this.backdrop.nativeElement.style.display = 'none';
      this.clearForm();
    }
  }

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private imageUploadService: ImageUploadService,
    private firestore: AngularFirestore,
    public loadingSpinnerService: LoadingSpinnerService,
    private cdr: ChangeDetectorRef,
    private giftCardService: GiftCardsService,
    private renderer: Renderer2,
    private formatDate: DateFormatterService,
    private storage: AngularFireStorage
  ) {
    this.giftCardForm = this.fb.group({
      giftCardTitle: ['', [Validators.required, Validators.maxLength(30)]],
      giftCardDetails: ['', [Validators.required, Validators.maxLength(140)]],
      giftCardType: ['', Validators.required],
      giftExpirationDate: [],
      giftActivationCode: ['', Validators.required]
    });
  }

  // Helper methods to access form controls
  get giftCardTitle(): AbstractControl {
    return this.giftCardForm.controls['giftCardTitle'];
  }

  get giftCardDetails(): AbstractControl {
    return this.giftCardForm.controls['giftCardDetails'];
  }

  get giftExpirationDate(): AbstractControl {
    return this.giftCardForm.controls['giftExpirationDate'];
  }

  onMediaChange(event: any) {
    const files = event.target.files;

    this.mediaValid = true;

    for (let file of files) {
      const isImage = file.type.startsWith('image/');
      let invalid = false;
      let errorMessage = '';
      let thumbnail: SafeUrl | string = '';

      if (isImage) {
        if (!['image/png', 'image/jpeg'].includes(file.type)) {
          invalid = true;
          errorMessage = 'Invalid image type. Only PNG and JPEG are allowed.';
        }

        // For valid images, process the thumbnail
        if (!invalid) {
          thumbnail = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
          this.validFiles.push(file);
          this.mediaUploaded = true;
        }

        this.selectedMedia.push({ name: file.name, size: file.size, invalid, errorMessage, thumbnail });
      } else {
        invalid = true;
        errorMessage = 'Invalid file type. Only images are allowed.';
      }

      if (invalid) {
        this.mediaValid = false;
      }
    }

    event.target.value = null;
  }

  removeFile(index: number) {
    const removedFile = this.selectedMedia.splice(index, 1)[0];

    if (!removedFile.invalid) {
      const fileIndex = this.validFiles.findIndex(file => file.name === removedFile.name);
      if (fileIndex !== -1) {
        this.validFiles.splice(fileIndex, 1);
      }
    }

    // Check media validity after removal
    this.mediaValid = this.selectedMedia.every(file => !file.invalid);

    if (this.selectedMedia.some(file => file.thumbnail)) {
      this.mediaUploaded = true;
    } else {
      this.mediaUploaded = false;
    }
  }

  async onAddGiftCard(isEditMode: boolean, card?: any) {

    if (this.giftCardForm.invalid) return;
    this.loadingSpinnerService.show();

    let uploadedImageUrl;
    const sessionBusiness: any = localStorage.getItem("businessInfo");
    const businessSessionData: any = JSON.parse(sessionBusiness);
    const countryISO = businessSessionData?.location?.countryISO;

    if (this.validFiles.length > 0) {
      try {
        uploadedImageUrl = await this.imageUploadService.compressAndUploadFiles(
          'GiftCards',
          this.businessId,
          this.validFiles,
          countryISO 
        );
      } catch (error) {
        console.error('Error uploading files:', error);
        this.loadingSpinnerService.hide();
        return;
      }
    }
    if (isEditMode && card) {
      if (uploadedImageUrl) {
        // this.storage.refFromURL(card.imageUrl).delete();
        await this.imageUploadService.deleteFromAws(card.imageUrl, countryISO);

      }
      const updatedGiftCardData = {
        title: this.giftCardForm.get('giftCardTitle')?.value,
        description: this.giftCardForm.get('giftCardDetails')?.value,
        type: this.giftCardForm.get('giftCardType')?.value,
        expirationDate: new Date(this.giftCardForm.get('giftExpirationDate')?.value),
        activationCode: this.giftCardForm.get('giftActivationCode')?.value,
        imageUrl: uploadedImageUrl ? uploadedImageUrl[0].link : card.imageUrl,
      };

      await this.firestore.collection('GiftCards').doc(card.uid).update(updatedGiftCardData);
      this.clearForm();
      this.onGiftCardUpdated();
      this.loadingSpinnerService.hide();
    } else {
      const sessionBusiness: any = localStorage.getItem("businessData");
      const businessSessionData: any = JSON.parse(sessionBusiness);

      const expirationDateValue = this.giftCardForm.get('giftExpirationDate')?.value;
      const expirationDate = expirationDateValue ? new Date(expirationDateValue) : null;

      try {
        // Step 1: Upload the selected image
        const giftCardData = {
          createdAt: new Date(),
          title: this.giftCardForm.get('giftCardTitle')?.value,
          description: this.giftCardForm.get('giftCardDetails')?.value,
          businessId: this.businessId,
          expirationDate: expirationDate,
          imageUrl: uploadedImageUrl ? uploadedImageUrl[0].link : null,
          type: this.giftCardForm.get('giftCardType')?.value,
          active: true,
          businessBannerUrl: businessSessionData.businessBannerUrl,
          businessName: businessSessionData.name,
          activationCode: this.giftCardForm.get('giftActivationCode')?.value,
          businessImageUrl: businessSessionData.businessImageUrl,
          countryISO: businessSessionData.locations ? businessSessionData.locations[0].countryISO : businessSessionData.location.countryISO
        };

        const docRef = await this.firestore.collection('GiftCards').add(giftCardData);


        await this.firestore.collection('GiftCards').doc(docRef.id).update({
          uid: docRef.id
        });

        this.clearForm();
        // Clear the cache and reload  vouchers after adding a new one
        this.onGiftCardUpdated();

      } catch (error) {
        console.error('Error uploading image or creating document:', error);
      } finally {
        this.loadingSpinnerService.hide();
      }
    }
  }

  clearForm() {
    this.giftCardForm.reset();
    this.giftCardForm.patchValue({
      giftActivationCode: this.activationCode || ''
    });
    this.selectedMedia = [];
    this.mediaUploaded = false;
  }

  expandFormWithCardData(card: any) {
    this.editMode = true;
    this.selectedCard = card;

    this.expandForm();
    this.giftCardForm.patchValue({
      giftCardTitle: card.title,
      giftCardDetails: card.description,
      giftCardType: card.type,
      giftExpirationDate: card.expirationDate ? this.formatDate.formatForInput(card.expirationDate) : '',
      giftActivationCode: card.activationCode || this.activationCode || ''
    });

    this.selectedMedia.push({
      name: 'Uploaded Image',
      size: 0,
      invalid: false,
      errorMessage: '',
      thumbnail: this.sanitizer.bypassSecurityTrustUrl(card.imageUrl)
    });
    this.mediaUploaded = true;
  }

  expandForm() {
    const collapseElement = this.giftExpand.nativeElement;
    this.selectedMedia = [];
    this.validFiles = [];
    this.mediaUploaded = false;
    if (!collapseElement.classList.contains('show')) {
      this.renderer.addClass(collapseElement, 'collapsing');
      this.renderer.removeClass(collapseElement, 'collapse');

      setTimeout(() => {
        this.renderer.removeClass(collapseElement, 'collapsing');
        this.renderer.addClass(collapseElement, 'collapse');
        this.renderer.addClass(collapseElement, 'show');

        this.toggleBackdrop(true);
      }, 350);
    }
  }

  onGiftCardUpdated() {
    this.giftCardService.clearCachedGiftCards();
    this.newTab.emit(this.newTabStatus);
    this.giftCardAdded.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['giftCards'] && this.giftCards.length > 0) {
      this.cdr.detectChanges(); // Ensure view is updated
    }
    if (this.giftCardForm && changes['activationCode']) {
      this.giftCardForm.patchValue({
        giftActivationCode: this.activationCode || ''
      });
    }
  }

  ngOnInit(): void {
    if (this.activeTabFromParent) {
      this.isActiveTab = !this.activeTabFromParent;
    }
  }

}
